<template>
  <div class="school-autocomplete">
    <input
      type="text"
      v-model="schoolQuery"
      @input="searchSchools"
      placeholder="Search for a school..."
      autocomplete="off"
    />
    <div class="school-results" v-show="showResults">
      <div v-if="loading">Loading...</div>
      <div v-else-if="schools.length === 0 && schoolQuery.length > 0">No schools found</div>
      <div
        v-for="school in schools"
        :key="school.id"
        class="school-item"
        @click="selectSchool(school)"
      >
        {{ school.name }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {  defineComponent, ref, watch, onMounted, onUnmounted  } from 'vue'
import debounce from 'lodash/debounce'
// Import usePaymentForm to directly set form values
import { usePaymentForm } from '~/shared/use-payment-form'

interface School {
  id: string;
  name: string;
  shortId: string;
}


const props = withDefaults(defineProps<{
  initialValue?: string
}>(), {
  initialValue: ''
})

    // Get setValue from usePaymentForm
    const paymentForm = usePaymentForm()
    if (!paymentForm) {
      throw new Error('PaymentForm context not available')
    }
    const { setValue } = paymentForm

    const schoolQuery = ref(props.initialValue)
    const schools = ref<School[]>([])
    const loading = ref(false)
    const showResults = ref(false)
    const selectedSchool = ref<School | null>(null)

    const searchSchools = debounce(async () => {
      // ...existing search logic...
      if (schoolQuery.value.length < 2) {
        schools.value = []
        showResults.value = false
        return
      }

      loading.value = true
      showResults.value = true

      try {
        // Consider making the API endpoint a prop if it needs to be configurable
        const response = await fetch(`https://velocity2.ouapis.org/donation-forms/schools?q=${encodeURIComponent(schoolQuery.value)}`)
        const data = await response.json()
        schools.value = data.schools || []
      } catch (error) {
        console.error('Error fetching schools:', error)
        schools.value = []
      } finally {
        loading.value = false
      }
    }, 300)

    const selectSchool = (school: School) => {
      selectedSchool.value = school
      schoolQuery.value = school.name
      // Directly set form values
      setValue('custom.schoolName', school.name)
      setValue('custom.schoolId', school.id)
      setValue('custom.schoolShortId', school.shortId)
      // emit('school-selected', school) // Remove emit
      showResults.value = false
    }

    // ...existing handleClickOutside, onMounted, onUnmounted...
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.school-autocomplete')) {
        showResults.value = false
      }
    }

    onMounted(() => {
      if (typeof window !== 'undefined') {
        window.addEventListener('click', handleClickOutside)
      }
    })

    onUnmounted(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('click', handleClickOutside)
      }
    })

    // Watch for changes in the input field if the user clears it after selecting
    watch(schoolQuery, (newValue) => {
      if (newValue === '') {
        selectedSchool.value = null;
        // Directly clear form values
        setValue('custom.schoolName', '')
        setValue('custom.schoolId', '')
        setValue('custom.schoolShortId', '')
        // emit('school-cleared'); // Remove emit
      } else if (selectedSchool.value && newValue !== selectedSchool.value.name) {
        // If user types again after selecting, reset selection and search
        selectedSchool.value = null;
        // Directly clear form values
        setValue('custom.schoolName', '')
        setValue('custom.schoolId', '')
        setValue('custom.schoolShortId', '')
        // emit('school-cleared'); // Remove emit
        searchSchools();
      }
    });

    onUnmounted(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('click', handleClickOutside)
      }
    })

    // Watch for changes in the input field if the user clears it after selecting
    watch(schoolQuery, (newValue) => {
      if (newValue === '') {
        selectedSchool.value = null;
        // Directly clear form values
        setValue('custom.schoolName', '')
        setValue('custom.schoolId', '')
        setValue('custom.schoolShortId', '')
        // emit('school-cleared'); // Remove emit
      } else if (selectedSchool.value && newValue !== selectedSchool.value.name) {
        // If user types again after selecting, reset selection and search
        selectedSchool.value = null;
        // Directly clear form values
        setValue('custom.schoolName', '')
        setValue('custom.schoolId', '')
        setValue('custom.schoolShortId', '')
        // emit('school-cleared'); // Remove emit
        searchSchools();
      }
    });


</script>
<style scoped lang="scss">
.school-autocomplete {
  position: relative;
  width: 100%;
}

.school-results {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.school-item {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.school-item:hover {
  background-color: #f0f0f0;
}

input[type="text"] {
  box-sizing: border-box;
  height: 44px;
  width: 100%;
  display: block;
  border: 1px solid #ddd;
  padding: 0 10px;
  border-radius: 4px;
  background: #f7f9fb;
  transition: all 0.3s ease-in-out;
  font-size: 16px;
  color: #222;

  &:hover {
    box-shadow: none;
    border-color: #385da3;
  }

  &:focus {
    border-color: #385da3;
    box-shadow: none;
    color: #222;
    background: white;
  }

  @media only screen and (min-width: 720px) {
    height: 42px;
  }
}
</style>
